import { Component, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Global error boundary. Without this, any exception thrown during render
 * (e.g. an undefined config field) unmounts the whole React tree and leaves
 * a blank/black screen with no diagnosic. This catches it and shows the
 * actual error + stack so the failure is visible and fixable.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error('[ErrorBoundary]', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="page-pad" style={{ padding: 32, color: 'var(--danger, #e00)' }}>
          <h1>{this.props.t('errorBoundary.somethingWentWrong')}</h1>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: 'rgba(255,0,0,0.06)',
              padding: 16,
              borderRadius: 8,
              fontSize: 'var(--font-sm)',
              overflowX: 'auto',
            }}
          >
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
          <button
            className="btn btn-primary"
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 16 }}
          >
            {this.props.t('errorBoundary.tryAgain')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);